import { use, useCallback, useEffect, useRef } from "react"
import _ from "lodash"
import { cid24, exportDateOfServiceInfoString, exportMemberInfoString, exportMemberName, proposeFormSaveDraft } from "@fusion-advantage/formlib"
import { useFormDispatch, useFormSelector } from "../../../redux/formStoreHooks"
import { useFormAppRelay } from "../../../lib/relay/FormAppRelay"
import { selectFormDisplayIdentity } from "../../../lib/relay/AppFormRelay"
import { RenderMedia, UserRole } from '@fusion-advantage/backcompat'
import { selectForm } from "../../../redux/selectForm"
import { V2L3AppHeader } from "./V2L3AppHeader"
import { V2L3FormAppHeaderAction, V2L3FormAppHeaderContent } from "./V2L3FormAppHeaderContent"
import { V2L3FormAppHeaderNavItems } from "./V2L3FormAppHeaderNav"
import { setPageIdx } from "../../../redux/formSlice"
import { V2L3FormPrintAppHeaderContent } from "./V2L3FormPrintAppHeaderContent"

export const V2LxFormAppHeader = () => {
	const instanceId = useRef<string>(cid24('i'))
	const formAppRelay = useFormAppRelay()
	const dispatch = useFormDispatch()
	const formLoading = useFormSelector(state => state.render.formLoading)
	const importLoading = useFormSelector(state => state.render.importLoading)
	const form = useFormSelector(state => state.render.form)
	const media = useFormSelector(state => state.render.media)
	const displayIdentity = useFormSelector(state => selectFormDisplayIdentity(state))
	const previewMode = useFormSelector(state => state.render.previewMode)
	const canSaveDraft = form?.export ? proposeFormSaveDraft(form.writeMode, form.export.Form.Encounter.CodeNext).isOk() : false
	const showShmoopie = Boolean(form && media !== RenderMedia.Print)
	let canPrint = false
	if(displayIdentity?.roles.includes(UserRole.PRINT_MEDICAL_RECORDS) && !importLoading) {
		canPrint = true
	}
	let screenAction = V2L3FormAppHeaderAction.BackToDashboard
	if(importLoading) {
		screenAction = V2L3FormAppHeaderAction.Loading
	} else if(previewMode) {
		screenAction = V2L3FormAppHeaderAction.None
	} else if(canSaveDraft) {
		screenAction = V2L3FormAppHeaderAction.SaveDraft
	}
	useEffect(() => {
		if(showShmoopie) {
			formAppRelay.updateRenderState({t1NavAvailableFor: {[instanceId.current]: true}})
			return () => { formAppRelay.updateRenderState({t1NavAvailableFor: {[instanceId.current]: false}}) }
		}
	}, [showShmoopie, formAppRelay])
	const handlePrintClick = useCallback(() => {
		formAppRelay.updateRenderState({media: RenderMedia.Print})
	}, [formAppRelay])
	const handleExitPrintClick = useCallback(() => {
		formAppRelay.updateRenderState({media: RenderMedia.Screen})
	}, [formAppRelay])
	const handleScreenActionClick = useCallback(() => {
		switch(screenAction) {
			case V2L3FormAppHeaderAction.SaveDraft:
				formAppRelay.saveFormDraft({closeOnSuccess: true})
				break
			case V2L3FormAppHeaderAction.BackToDashboard:
				formAppRelay.exitForm()
				break
		}
	}, [screenAction, formAppRelay])
	const { pages } = useFormSelector(selectForm)
	const activePageIdx = useFormSelector(state => state.form.activePageIdx)
	const handleNavChange = useCallback((idx: number) => {
		dispatch(setPageIdx(idx))
	}, [dispatch])
	if(formLoading || !form) {
		return (
			<V2L3AppHeader />
		)
	}
	if(media === RenderMedia.Print) {
		return (
			<V2L3AppHeader
				hasPhantomShmoopie={false}
				row1Children={
					<V2L3FormPrintAppHeaderContent
						onExitClick={handleExitPrintClick}
					/>
				}
			/>
		)
	}
	return (
		<V2L3AppHeader
			hasPhantomShmoopie={showShmoopie}
			row1Children={
				<V2L3FormAppHeaderContent
					memberName={exportMemberName(form.export)}
					memberInfo={exportMemberInfoString(form.export)}
					dateOfServiceInfo={exportDateOfServiceInfoString(form.export)}
					canPrint={canPrint}
					onPrintClick={handlePrintClick}
					action={screenAction}
					onActionClick={handleScreenActionClick}
				/>
			}
			row2Children={
				<V2L3FormAppHeaderNavItems
					activeIdx={activePageIdx}
					pages={pages}
					onChange={handleNavChange}
				/>
			}
		>
		</V2L3AppHeader>
	)
}
